// Router tunggal (menggantikan router.tsx + login internal App lama).
// RequireAuth membungkus AppShell; tiap route dijaga permission (Guard) —
// akun tanpa izin diarahkan ke halaman pertama yang boleh.

import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { defaultPath } from "../lib/nav";
import { AppShell } from "./shell/AppShell";

// Tiap halaman di-lazy-load agar bundle utama kecil (code-split per route).
// Named export → map ke { default } supaya kompatibel dengan React.lazy.
const LoginPage = lazy(() => import("../features/auth/LoginPage").then((m) => ({ default: m.LoginPage })));
const CashierPage = lazy(() => import("../features/cashier/CashierPage").then((m) => ({ default: m.CashierPage })));
const SalesHistoryPage = lazy(() => import("../features/history/SalesHistoryPage").then((m) => ({ default: m.SalesHistoryPage })));
const CashShiftPage = lazy(() => import("../features/shift/CashShiftPage").then((m) => ({ default: m.CashShiftPage })));
const ProductsPage = lazy(() => import("../features/catalog/ProductsPage").then((m) => ({ default: m.ProductsPage })));
const StockPage = lazy(() => import("../features/catalog/StockPage").then((m) => ({ default: m.StockPage })));
const RestockPage = lazy(() => import("../features/restock/RestockPage").then((m) => ({ default: m.RestockPage })));
const ReportsPage = lazy(() => import("../features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const SupplierPage = lazy(() => import("../features/master/SupplierPage").then((m) => ({ default: m.SupplierPage })));
const CustomerPage = lazy(() => import("../features/master/CustomerPage").then((m) => ({ default: m.CustomerPage })));
const SettingsPage = lazy(() => import("../features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const FilesPage = lazy(() => import("../features/storage/FilesPage").then((m) => ({ default: m.FilesPage })));

function Fullscreen({ children }: { children: React.ReactNode }) {
  return <div className="grid min-h-screen place-items-center text-muted">{children}</div>;
}

function RequireAuth() {
  const { status } = useAuth();
  if (status === "loading") return <Fullscreen>Memuat…</Fullscreen>;
  if (status === "anonymous") return <Navigate to="/login" replace />;
  return <AppShell />;
}

function Guard({ anyOf, children }: { anyOf: string[]; children: React.ReactNode }) {
  const { can } = useAuth();
  const allowed = anyOf.length === 0 || anyOf.some((permission) => can(permission));
  return allowed ? <>{children}</> : <Navigate to={defaultPath(can)} replace />;
}

function HomeRedirect() {
  const { can } = useAuth();
  return <Navigate to={defaultPath(can)} replace />;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      {/* Suspense untuk route di luar shell (login); halaman dalam shell punya
          Suspense sendiri di AppShell agar shell tetap terpasang. */}
      <Suspense fallback={<Fullscreen>Memuat…</Fullscreen>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireAuth />}>
          <Route index element={<HomeRedirect />} />
          <Route path="/cashier" element={<Guard anyOf={["sale.create"]}><CashierPage /></Guard>} />
          <Route path="/history" element={<Guard anyOf={["report.sales.view"]}><SalesHistoryPage /></Guard>} />
          <Route path="/cash" element={<Guard anyOf={["cash.movement"]}><CashShiftPage /></Guard>} />
          <Route path="/products" element={<Guard anyOf={["product.manage"]}><ProductsPage /></Guard>} />
          <Route path="/stock" element={<Guard anyOf={["lot.manage"]}><StockPage /></Guard>} />
          <Route path="/restock" element={<Guard anyOf={["lot.manage"]}><RestockPage /></Guard>} />
          <Route path="/reports" element={<Guard anyOf={["report.sales.view"]}><ReportsPage /></Guard>} />
          <Route path="/suppliers" element={<Guard anyOf={["supplier.manage"]}><SupplierPage /></Guard>} />
          <Route path="/customers" element={<Guard anyOf={["customer.manage"]}><CustomerPage /></Guard>} />
          {/* /files + /files/<prefix> — prefix folder jadi path (splat) agar reload tetap di folder yang sama. */}
          <Route path="/files/*" element={<Guard anyOf={["storage.manage"]}><FilesPage /></Guard>} />
          {/* /settings + /settings/:section — tiap sub-halaman punya URL sendiri
              agar reload tetap di halaman yang sama. */}
          {["/settings", "/settings/:section"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Guard
                  anyOf={[
                    "settings.edit",
                    "discount.manage",
                    "settings.users.manage",
                    "settings.cipherKey.edit",
                    "branch.manage",
                    "branch.price.manage",
                    "stock.transfer",
                    "channel.manage",
                  ]}
                >
                  <SettingsPage />
                </Guard>
              }
            />
          ))}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
